// Domain Service - Real API integration for domain management
// Connects to backend Express API for all domain operations

import { apiClient } from './apiClient';

// Import authoritative Domain interface from data layer
import { Domain } from '../types/dataLayer';

// Note: All domain operations use the authoritative Domain interface from '../types/dataLayer'
// Backend API is expected to return data matching the DATA_ACCESS_LAYER_DOCUMENTATION.md schema

export interface DomainStatistics {
  total: number;
  pending: number;
  approved: number;
  active: number;
  suspended: number;
  rejected: number;
}

export interface PolicyChange {
  domain: string;
  changeType: 'add_domain' | 'remove_domain' | 'update_policy' | 'bulk_update';
  status: 'pending' | 'applied' | 'failed' | 'rolled_back';
  requestedBy: string;
  appliedBy?: string;
  requestedAt: string;
  appliedAt?: string;
  teamsCommand: string;
  errorMessage?: string;
}

class DomainService {
  // Get all domains with optional filtering
  async getDomains(filters?: {
    status?: string;
    source?: string;
    search?: string;
  }): Promise<Domain[]> {
    try {
      const params: Record<string, string> = {};
      if (filters?.status) params.status = filters.status;
      if (filters?.source) params.source = filters.source;
      if (filters?.search) params.search = filters.search;

      const response = await apiClient.get<Domain[]>('/domains', params);
      return response.data;
    } catch (error) {
      // Silently fail and throw user-friendly error
      throw new Error('Failed to load domains. Please try again.');
    }
  }

  // Get pending domains for approval
  async getPendingDomains(): Promise<Domain[]> {
    try {
      const response = await apiClient.get<Domain[]>('/domains/pending');
      return response.data;
    } catch (error) {
      // Silently fail and throw user-friendly error
      throw new Error('Failed to load pending domains. Please try again.');
    }
  }

  // Get domain statistics
  async getDomainStatistics(): Promise<DomainStatistics> {
    try {
      const response = await apiClient.get<{
        total: number;
        byStatus: Record<string, number>;
        bySource: Record<string, number>;
        byRiskLevel: Record<string, number>;
      }>('/domains/statistics');
      
      const backendStats = response.data;
      
      // Transform backend format to frontend format
      return {
        total: backendStats.total || 0,
        pending: backendStats.byStatus?.pending || 0,
        approved: backendStats.byStatus?.approved || 0,  
        active: backendStats.byStatus?.active || 0,
        suspended: backendStats.byStatus?.suspended || 0,
        rejected: backendStats.byStatus?.rejected || 0,
      };
    } catch (error) {
      // No fallbacks - throw the error properly
      throw new Error('Failed to load domain statistics. Please try again.');
    }
  }

  // Add new domain manually
  async addDomain(domain: string, source: 'manual' | 'policy_requirement' = 'manual'): Promise<Domain> {
    try {
      const response = await apiClient.post<Domain>('/domains', {
        domain,
        source
      });
      return response.data;
    } catch (error) {
      console.error('Failed to add domain:', error);
      throw new Error('Failed to add domain. Please check the domain format and try again.');
    }
  }

  // Approve domain (changes approval_status to 'approved')
  async approveDomain(domain: string, source: string): Promise<void> {
    try {
      await apiClient.patch(`/api/domains/${encodeURIComponent(domain)}/approve`);
    } catch (error) {
      console.error('Failed to approve domain:', error);
      throw new Error('Failed to approve domain. Please try again.');
    }
  }

  // Reject domain (changes approval_status to 'rejected')
  async rejectDomain(domain: string, source: string, reason?: string): Promise<void> {
    try {
      await apiClient.patch(`/api/domains/${encodeURIComponent(domain)}/reject`, {
        reason
      });
    } catch (error) {
      console.error('Failed to reject domain:', error);
      throw new Error('Failed to reject domain. Please try again.');
    }
  }

  // Suspend multiple domains (changes status to 'suspended' and removes from policies)
  async suspendDomains(domains: string[], reason?: string): Promise<void> {
    try {
      await apiClient.patch('/api/domains/suspends', {
        domains,
        reason
      });
    } catch (error) {
      console.error('Failed to suspend domains:', error);
      throw new Error('Failed to suspend domains. Please try again.');
    }
  }

  // Reactivate domain (changes status from 'suspended' to 'active')
  async reactivateDomain(domain: string, reason?: string): Promise<void> {
    try {
      await apiClient.patch(`/api/domains/${encodeURIComponent(domain)}/reactivate`, {
        reason
      });
    } catch (error) {
      console.error('Failed to reactivate domain:', error);
      throw new Error('Failed to reactivate domain. Please try again.');
    }
  }

  // Get policy changes
  async getPolicyChanges(filters?: {
    status?: string;
    domain?: string;
  }): Promise<PolicyChange[]> {
    try {
      const params: Record<string, string> = {};
      if (filters?.status) params.status = filters.status;
      if (filters?.domain) params.domain = filters.domain;

      const response = await apiClient.get<PolicyChange[]>('/policies/changes', params);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch policy changes:', error);
      throw new Error('Failed to load policy changes. Please try again.');
    }
  }

  // Confirm removal of approved domains
  async confirmDomainRemoval(domains: string[]): Promise<{ domainsRemoved: number; errors: string[] }> {
    try {
      const response = await apiClient.post<{ domainsRemoved: number; errors: string[] }>('/domains/confirm-removal', {
        domains
      });
      return response.data;
    } catch (error) {
      console.error('Failed to confirm domain removal:', error);
      throw new Error('Failed to remove domains. Please try again.');
    }
  }
}

export const domainService = new DomainService();